import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Check, Play, Loader2 } from "lucide-react";

// Image from public folder
const heroImage = "/assets/imgs/hero-vsl-creator.webp";

interface LeadsSceneProps {
  isActive: boolean;
}

type AnimationState = 
  | "idle"
  | "typing-email"
  | "hovering-cta"
  | "submitting"
  | "success";

const MOCK_NAME = "João Silva";
const MOCK_EMAIL = "joao.silva@gmail.com";

const TYPING_DELAYS = {
  minDelay: 30,
  maxDelay: 70,
  pauseChance: 0.08,
  pauseLength: 100,
};

// Helper to sleep
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Fake cursor component
function FakeCursor({ x, y, visible, clicking }: { x: number; y: number; visible: boolean; clicking: boolean }) {
  useLanguage();
  return (
    <div 
      className={cn(
        "absolute z-50 pointer-events-none transition-all duration-300 ease-out",
        visible ? "opacity-100" : "opacity-0",
        clicking && "scale-90"
      )}
      style={{ left: x, top: y }}
    >
      <svg width="20" height="26" viewBox="0 0 20 26" fill="none">
        <path 
          d="M1 1L1 19.5L5.5 15L10.5 24L13.5 22.5L8.5 13.5L15 13.5L1 1Z" 
          fill="white" 
          stroke="#1F2937" 
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

// Success toast component
function SuccessToast({ visible }: { visible: boolean }) {
  useLanguage();
  return (
    <div className={cn(
      "absolute top-3 left-1/2 -translate-x-1/2 z-50",
      "bg-white rounded-lg shadow-lg border border-gray-100",
      "px-3 py-2 flex items-center gap-2",
      "transition-all duration-300",
      visible 
        ? "opacity-100 translate-y-0" 
        : "opacity-0 -translate-y-2 pointer-events-none"
    )}>
      <div className="w-4 h-4 rounded-full bg-[#1F6BFF]/10 flex items-center justify-center">
        <Check className="w-2.5 h-2.5 text-[#1F6BFF]" />
      </div>
      <span className="text-[10px] font-medium text-gray-700">
        {translateInterfaceText("Lead capturado com sucesso")}</span>
    </div>
  );
}

// Lead saved mini panel
function LeadSavedMini({ visible }: { visible: boolean }) {
  useLanguage();
  return (
    <div className={cn(
      "absolute bottom-3 right-3 z-40",
      "bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-gray-100",
      "px-3 py-2 transition-all duration-300",
      visible 
        ? "opacity-100 translate-y-0" 
        : "opacity-0 translate-y-2 pointer-events-none"
    )}>
      <p className="text-[9px] font-semibold text-gray-700">{translateInterfaceText("Novo lead: João Silva")}</p>
      <p className="text-[8px] text-gray-500">{translateInterfaceText("Status: Enviado para CRM")}</p>
    </div>
  );
}

// Typewriter input component
function TypewriterInput({ 
  label, 
  placeholder,
  value, 
  isFocused,
  showSuccess,
  isTyping
}: { 
  label: string;
  placeholder: string;
  value: string; 
  isFocused: boolean;
  showSuccess: boolean;
  isTyping: boolean;
}) {
  useLanguage();
  return (
    <div className="space-y-1">
      <label className="text-[9px] font-medium text-gray-600 uppercase tracking-wide">
        {label}
      </label>
      <div 
        className={cn(
          "relative h-8 px-2.5 rounded-lg text-[11px] flex items-center transition-all duration-200",
          isFocused 
            ? "bg-white border-[#1F6BFF] shadow-[0_0_0_3px_rgba(31,107,255,0.1)]" 
            : "bg-gray-50 border-gray-200",
          showSuccess && "border-[#1F6BFF]",
          "border"
        )}
      >
        {value ? (
          <span className="text-gray-800">
            {value}
            {isTyping && <span className="animate-pulse ml-0.5">|</span>}
          </span>
        ) : (
          <span className="text-gray-400">{placeholder}</span>
        )}
        {showSuccess && (
          <Check className="absolute right-2 w-3 h-3 text-[#1F6BFF]" />
        )}
      </div>
    </div>
  );
}

export function LeadsScene({ isActive }: LeadsSceneProps) {
  useLanguage();
  const [animState, setAnimState] = useState<AnimationState>("idle");
  const [nameValue, setNameValue] = useState(MOCK_NAME);
  const [emailValue, setEmailValue] = useState("");
  const [cursorPos, setCursorPos] = useState({ x: 200, y: 50 });
  const [cursorVisible, setCursorVisible] = useState(false);
  const [cursorClicking, setCursorClicking] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const animationRef = useRef<boolean>(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  // Simulate typing with human-like delays
  const simulateTyping = useCallback(async (
    text: string, 
    setter: React.Dispatch<React.SetStateAction<string>>
  ): Promise<void> => {
    for (const char of text) {
      if (!animationRef.current) return;
      
      const delay = Math.random() * (TYPING_DELAYS.maxDelay - TYPING_DELAYS.minDelay) + TYPING_DELAYS.minDelay;
      await sleep(delay);
      
      if (Math.random() < TYPING_DELAYS.pauseChance) {
        await sleep(TYPING_DELAYS.pauseLength);
      }
      
      setter(prev => prev + char);
    }
  }, []);

  // Main animation sequence
  useEffect(() => {
    if (!isActive) {
      animationRef.current = false;
      setAnimState("idle");
      setNameValue(MOCK_NAME);
      setEmailValue("");
      setCursorVisible(false);
      setOverlayVisible(false);
      return;
    }

    animationRef.current = true;

    const runAnimation = async () => {
      // Reset - keep name pre-filled
      setEmailValue("");
      setCursorVisible(false);
      setCursorClicking(false);

      // 0.0s - Overlay appears
      await sleep(100);
      setOverlayVisible(true);

      // 0.2s - Cursor appears (desktop only)
      await sleep(100);
      if (!isMobile) {
        setCursorPos({ x: 200, y: 50 });
        setCursorVisible(true);
      }

      // 0.4s - Move directly to Email field
      await sleep(200);
      if (!isMobile) setCursorPos({ x: 80, y: 190 });

      // 0.6s - Click on Email input
      await sleep(200);
      if (!isMobile) setCursorClicking(true);
      await sleep(80);
      if (!isMobile) setCursorClicking(false);
      setAnimState("typing-email");

      // 0.7s - Start typing email
      await sleep(100);
      if (!animationRef.current) return;
      await simulateTyping(MOCK_EMAIL, setEmailValue);

      // ~2.2s - Move to CTA
      await sleep(200);
      if (!animationRef.current) return;
      if (!isMobile) setCursorPos({ x: 140, y: 260 });
      setAnimState("hovering-cta");

      // ~2.4s - Click CTA
      await sleep(200);
      if (!isMobile) setCursorClicking(true);
      await sleep(100);
      if (!isMobile) setCursorClicking(false);
      setAnimState("submitting");

      // ~2.7s - Success
      await sleep(500);
      if (!animationRef.current) return;
      setAnimState("success");
      if (!isMobile) setCursorVisible(false);

      // ~3.2s - Hold success state
      await sleep(1300);
    };

    runAnimation();

    return () => {
      animationRef.current = false;
    };
  }, [isActive, isMobile, simulateTyping]);

  return (
    <div className="h-full w-full p-4 flex flex-col">
      {/* Video background container */}
      <div className="relative flex-1 rounded-xl overflow-hidden">
        {/* Video thumbnail */}
        <img 
          src={heroImage} 
          alt={translateInterfaceText("Video thumbnail")}
          className="absolute inset-0 w-full h-full object-cover"
        />
        
        {/* Gradient overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 100%)'
          }}
        />

        {/* Play button (behind form) */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-40">
            <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
          </div>
        </div>

        {/* Form overlay */}
        <div 
          className={cn(
            "absolute inset-0 flex items-center justify-center p-3 transition-all duration-300",
            overlayVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
          )}
        >
          <div 
            className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl w-full max-w-[240px] sm:max-w-[260px] p-4"
            style={{
              boxShadow: '0 20px 50px -12px rgba(0,0,0,0.4)'
            }}
          >
            {/* Title */}
            <h3 className="text-[11px] sm:text-[12px] font-bold text-gray-800 leading-tight mb-1">
              {translateInterfaceText("Acesse o conteúdo e receba o link no seu e-mail")}</h3>
            
            {/* Subtitle */}
            <p className="text-[9px] text-gray-500 mb-4">
              {translateInterfaceText("Preencha abaixo para continuar assistindo.")}</p>

            {/* Form fields */}
            <div className="space-y-3">
              <TypewriterInput
                label={translateInterfaceText("Nome")}
                placeholder={translateInterfaceText("Ex: João Silva")}
                value={nameValue}
                isFocused={false}
                showSuccess={nameValue.length > 0}
                isTyping={false}
              />
              
              <TypewriterInput
                label={translateInterfaceText("E-mail")}
                placeholder={translateInterfaceText("Ex: joao@email.com")}
                value={emailValue}
                isFocused={animState === "typing-email"}
                showSuccess={animState === "success" && emailValue.length > 0}
                isTyping={animState === "typing-email"}
              />
            </div>

            {/* CTA Button */}
            <button
              className={cn(
                "w-full mt-4 h-9 rounded-lg text-[11px] font-semibold text-white",
                "transition-all duration-200 flex items-center justify-center gap-2",
                animState === "hovering-cta" && "bg-[#143FC0] -translate-y-0.5",
                animState === "submitting" && "bg-[#1F6BFF]/90",
                animState === "success" && "bg-[#1F6BFF]",
                animState !== "hovering-cta" && animState !== "submitting" && animState !== "success" && "bg-[#1F6BFF]"
              )}
            >
              {animState === "submitting" ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>{translateInterfaceText("Enviando...")}</span>
                </>
              ) : animState === "success" ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>{translateInterfaceText("Enviado")}</span>
                </>
              ) : (
                translateInterfaceText("Continuar e Assistir")
              )}
            </button>

            {/* Skip link */}
            <div className="mt-2 text-right">
              <span className="text-[9px] text-gray-400 hover:text-gray-600 cursor-pointer transition-colors">
                {translateInterfaceText("Pular →")}</span>
            </div>
          </div>
        </div>

        {/* Fake cursor (desktop only) */}
        {!isMobile && (
          <FakeCursor 
            x={cursorPos.x} 
            y={cursorPos.y} 
            visible={cursorVisible} 
            clicking={cursorClicking} 
          />
        )}

        {/* Success toast */}
        <SuccessToast visible={animState === "success"} />

        {/* Lead saved mini panel */}
        <LeadSavedMini visible={animState === "success"} />
      </div>
    </div>
  );
}
